import React from 'react';
import LoginModal from '../Login/LoginModal';
import ShareTextModal from '../common/ShareTextModal';
import { API_BASE_URL } from '../../lib/constants';
import { generateDownloadFilename } from '../../utils/fileUtils';
import { handleUnifiedError } from '../../utils/unifiedErrorHandler';
import SizeSelector from '../AiToolTemplate/components/SizeSelector/SizeSelector';

const inspirationTags = [
  'Landscape',
  'Portrait',
  'Architecture',
  'Art',
  'SciFi',
  'Nature',
  'Animals',
  'Creative'
];

const TextToImagePanelView = ({
  t,
  prompt,
  setPrompt,
  styles,
  selectedStyle,
  setSelectedStyle,
  selectedSize,
  setSelectedSize,
  generationCount,
  creditsPerImage,
  isGenerating,
  isLoadingModels,
  generationProgress,
  handleGenerateImage,
  generatedImage,
  shareOpen,
  setShareOpen,
  shareMediaUrl,
  showLoginModal,
  handleCloseLogin
}) => {
  const handleTagClick = (tag) => {
    const tagKey = tag.toLowerCase().replace('-', '');
    const tagPrompt = t(`aiWorkbench.tagPrompts.${tagKey}`);
    setPrompt(tagPrompt || tag);
  };

  const completedImageUrl = () => `${API_BASE_URL}${generatedImage.url}`

  const handleImageClick = () => {
    if (!generatedImage || !generatedImage.url) return;
    const url = generatedImage.url.startsWith('http')
      ? generatedImage.url
      : completedImageUrl();
    if (url) window.open(url, '_blank');
  };

  const handleDownload = () => {
    if (!generatedImage || !generatedImage.url) return;
    const downloadUrl = generatedImage.url.startsWith('http')
      ? generatedImage.url
      : completedImageUrl();

    fetch(downloadUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const filename = generateDownloadFilename(generatedImage.id);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        handleUnifiedError(error, t, {
          showToast: true,
          fallbackMessage: t('aiWorkbench.downloadImageError')
        });
      });
  };

  return (
    <div className="text-to-image-panel">
      <div className="inspiration-panel">
        <h3>{t('aiWorkbench.inspiration')}</h3>
        <p>{t('aiWorkbench.inspirationDescription')}</p>
        <div className="tags-container">
          {inspirationTags.map((tag) => (
            <button
              key={tag}
              className="tag-btn"
              onClick={() => handleTagClick(tag)}
            >
              {t(`aiWorkbench.tags.${tag.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="generation-panel">
        <div className="panel-header">
          <h3>{t('aiWorkbench.aiImageGeneration')}</h3>
          <span className="mode-badge">{t('aiWorkbench.textToImageMode')}</span>
        </div>
        <textarea
          placeholder={t('aiWorkbench.promptPlaceholder')}
          maxLength="5000"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="style-selection">
          <h4>{t('aiWorkbench.style')}</h4>
          <div className="style-options">
            {styles.map((style) => (
              <button
                key={style.id}
                className={`style-option ${
                  selectedStyle === style.id ? 'active' : ''
                }`}
                onClick={() => {
                  if (selectedStyle === style.id) {
                    setSelectedStyle('');
                  } else {
                    setSelectedStyle(style.id);
                  }
                }}
              >
                {style.name}
              </button>
            ))}
          </div>
        </div>
        <div className="size-selection">
          <SizeSelector
            id="textToImageSize"
            title={t('aiWorkbench.aspectRatio')}
            required
            value={selectedSize}
            onChange={setSelectedSize}
            disabled={isGenerating || isLoadingModels}
            props={{ defaultValue: 'Auto' }}
          />
        </div>
        <div className="generation-options">
          <span>{t('aiWorkbench.generationCount')}</span>
          <div className="options-right">
            <button className="count-btn">
              {generationCount} {t('aiWorkbench.images')}
            </button>
            <span className="credits">
              {generationCount * creditsPerImage} {t('aiWorkbench.credits')}
            </span>
          </div>
        </div>
        <p className="tip">{t('aiWorkbench.tip')}</p>
        <button
          className={`start-btn ${isGenerating ? 'generating' : ''}`}
          onClick={handleGenerateImage}
          disabled={isGenerating || !prompt.trim() || isLoadingModels}
          style={{
            background: isGenerating
              ? `linear-gradient(to right, #00b894 ${generationProgress}%, #666 ${generationProgress}%)`
              : '#00b894'
          }}
        >
          {isGenerating
            ? `${t('aiWorkbench.generating')} ${Math.round(
                generationProgress
              )}% (${generationCount * creditsPerImage} ${t('credits.credits')})`
            : `${t('aiWorkbench.startGeneration')}`}
        </button>
        {generatedImage && generatedImage.url && (
          <div className="generated-image" style={{ position: 'relative' }}>
            <h4>{t('aiWorkbench.generatedImage')}</h4>
            <div style={{ position: 'relative' }}>
              <img
                src={completedImageUrl()}
                alt="Generated"
                style={{ cursor: 'zoom-in' }}
                onClick={handleImageClick}
              />
              <button
                onClick={() => {
                  if (!shareMediaUrl) return;
                  setShareOpen(true);
                }}
                disabled={!shareMediaUrl}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid #444',
                  background: '#2a2a2a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: shareMediaUrl ? 'pointer' : 'not-allowed',
                  opacity: shareMediaUrl ? 1 : 0.6
                }}
                aria-label={t('creationRecord.share')}
              >
                <img
                  src="/icons/share.svg"
                  alt={t('creationRecord.share')}
                  style={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) invert(1)',
                    padding: 0
                  }}
                />
              </button>
            </div>
            <div className="image-actions">
              <button className="download-btn" onClick={handleDownload}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t('aiWorkbench.download')}
              </button>
            </div>
          </div>
        )}
      </div>
      <LoginModal isOpen={showLoginModal} onClose={handleCloseLogin} />
      <ShareTextModal
        open={shareOpen}
        title={t('creationRecord.enterShareText')}
        placeholder={t('creationRecord.enterShareText')}
        maxLength={2000}
        platform={null}
        mediaUrl={shareMediaUrl}
        mediaType="image"
        by=""
        onConfirm={() => {
          setShareOpen(false);
        }}
        onCancel={() => {
          setShareOpen(false);
        }}
      />
    </div>
  );
};

export default TextToImagePanelView;
